import { AuthService } from '@/services';
import { MFA } from '@consts';
import { redirectAfterJoin } from '@/utils/authRedirect';
import { setAdrielTokens } from '@/utils/utils';
import { gtm, GTM_EVENT_NAME } from '@/utils/tracker/googleTagManager.js';

/** A token-issuing auth response that stopped at the second factor. */
export const isMfaRequired = (res) =>
    res?.mfa_required === true && !!res.mfa_token;

/**
 * Keeps the challenge in memory and moves to `/sign/mfa`. `returnTo` tells the
 * challenge view where to go after a successful verify:
 * `{ source: 'login' | 'oauth' | 'reset' | 'verify', dest?, query? }`.
 */
export function startMfaChallenge({ store, router, res, returnTo }) {
    store.commit('user/setMfaChallenge', {
        token: res.mfa_token,
        methods: res.methods || [],
        // The server's `expires_at` is on the server clock. Comparing it with
        // this device's clock would bounce a user whose clock runs >5 min fast
        // straight back to the login form, even for backup codes. Count the TTL
        // from the moment the challenge arrived instead; the server still has
        // the final say (INVALID_MFA_CHALLENGE).
        expiresAt: new Date(
            Date.now() + MFA.CHALLENGE_TTL_SEC * 1000
        ).toISOString(),
        serverExpiresAt: res.expires_at,
        authSource: res.auth_source,
        continuation: res.continuation || null,
        returnTo,
    });
    return router.push({ name: MFA.ROUTE_NAME });
}

/**
 * Where a password login lands, shared by Login.js and the MFA challenge view.
 * Tracking side effects stay with the caller.
 */
export function redirectAfterLogin({ router, route, res, isMobileSize }) {
    const name = route.query.dest || 'MyCampaigns';
    let query = { ...route.query };

    if (name === 'Settings') {
        query.businessCert = 1;
    }

    const workspaceId = R.propOr(query.accountId, 'joined_workspace_id', res);
    query = R.assoc('accountId', workspaceId, query);
    query = R.assoc('redirected', 1, query);

    if (isMobileSize) {
        return router.push({ name: 'MobileDemo' });
    }
    return router.push({ name, query });
}

/**
 * `beforeEnter` for `/sign/mfa`: without a live challenge (reload, direct URL,
 * expired) the only way forward is signing in again.
 */
export const requireMfaChallenge = (store) => (to, from, next) => {
    if (store.getters['user/isMfaChallengeLive']()) {
        return next();
    }
    store.commit('user/clearMfaChallenge');
    return next({ name: 'Sign/Login' });
};

/**
 * Completes an OAuth or email-verify login: stores tokens, loads the session
 * and account list, then redirects. Shared by `Redirect.js` (direct) and
 * `useMfaChallenge` (after the second factor, source `oauth` / `verify`).
 *
 * `loginMethod` is the capitalized provider name for the gtm LOGIN_SUCCESS
 * event (e.g. "Google"); pass `undefined` to skip the event (verify flow).
 */
export async function finishOauthLogin({
    store,
    router,
    route,
    res,
    loginMethod,
    isMobileSize,
    isPlanT,
}) {
    if (loginMethod) {
        gtm.logEvent(GTM_EVENT_NAME.LOGIN_SUCCESS, {
            login_method: loginMethod,
        });
    }

    setAdrielTokens({
        token: res.token,
        refresh_token: res.refresh_token,
        expiration_time_token_seconds: res.expiration_time_token_seconds,
        expiration_time_refresh_seconds: res.expiration_time_refresh_seconds,
    });
    AuthService.setAccessToken$.next(res.token);

    await store.dispatch('user/checkSession');
    await store.dispatch('user/fetchAccountList');

    redirectAfterJoin({
        router,
        route,
        accounts: store.getters['user/accounts'],
        isMobileSize,
        isPlanT,
    });
}
